import { useState, useEffect, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { isNativeApp, decideHomeRoute } from "@/lib/native";

/**
 * Rota "/": sessão válida → /dashboard; app nativo sem sessão → /auth;
 * site sem sessão → página pública (ou /auth se a página estiver desligada).
 */
export function HomeRedirect({ home }: { home: ReactNode }) {
  const { user, loading } = useAuth();
  const [landingEnabled, setLandingEnabled] = useState<boolean | null>(null);
  const [checkingSettings, setCheckingSettings] = useState(true);

  useEffect(() => {
    const checkLandingPageSetting = async () => {
      try {
        const { data } = await supabase
          .from("system_settings")
          .select("value")
          .eq("key", "landing_page_enabled")
          .maybeSingle();
        setLandingEnabled(data?.value === true);
      } catch {
        setLandingEnabled(true);
      } finally {
        setCheckingSettings(false);
      }
    };
    checkLandingPageSetting();
  }, []);

  const target = decideHomeRoute({
    loading: loading || checkingSettings,
    hasUser: !!user,
    isNative: isNativeApp(),
    landingEnabled,
  });

  if (target === "wait") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="home-redirect-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (target === "home") return <>{home}</>;
  return <Navigate to={target} replace />;
}
